import axios from 'axios'

// Background queue for image uploads (POST {shop}/files/new.json).
//
// Uploads live here instead of in the editor components, so they keep running
// when the user switches tabs or closes the editor. The serializable job state
// is mirrored to the Vuex `uploads` list for the upload tray and the editors;
// the File/Blob objects, abort controllers and promises stay in this module.
//
// Every job may have an owner, e.g. 'product:123', and a slot ('main', 'mobile',
// 'gallery', ...) telling the owning editor where the uploaded file goes. An
// editor that is open applies the result straight away (lib/uploadaware.js).
// Otherwise the result waits as a pending attachment until the editor is
// opened again. Jobs without an owner are plain uploads to the file library.

const MAX_CONCURRENT = 2
const CLEAR_DONE_DELAY = 4000
const ACTIVE_STATUSES = ['queued', 'fetching', 'uploading', 'processing']

let store = null
let eventBus = null
let sequence = 0
let running = 0
const waiting = []
const jobs = new Map()

export function initUploadQueue (nuxtStore, bus) {
  store = nuxtStore
  eventBus = bus
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (event) => {
      if (hasActive()) {
        event.preventDefault()
        event.returnValue = ''
      }
    })
  }
}

function update (id, changes) {
  store.commit('updateUpload', { id, ...changes })
}

function stateOf (id) {
  return store.state.uploads.find(u => u.id === id)
}

function extensionOf (filename) {
  return filename && filename.includes('.') ? filename.split('.').pop().toLowerCase() : ''
}

function errorMessage (e) {
  if (e && e.response && e.response.data) {
    const data = e.response.data
    if (typeof data === 'string') {
      return data
    }
    // The backend sends errorMessage (CatchableRESTException::sendJsonStatus)
    return data.errorMessage || data.error || data.message || e.message
  }
  return e && e.message ? e.message : String(e)
}

function isCanceled (e) {
  return axios.isCancel(e) || (e && (e.name === 'CanceledError' || e.name === 'AbortError'))
}

function createPromise (job) {
  job.promise = new Promise((resolve, reject) => {
    job.resolve = resolve
    job.reject = reject
  })
  // Nobody has to wait for an upload, so failures must not be reported as unhandled
  job.promise.catch(() => {})
}

/**
 * Add an upload to the queue.
 *
 * @param source  File or Blob, an async function (signal) => ({ blob, name })
 *                that fetches the image first, or { import: payload } for an
 *                image the server fetches itself (files/import.json, e.g.
 *                { filmikamari: asset id, filename })
 * @param options owner: owning entity key or null, ownerLabel: name shown in the
 *                upload tray, open: showDetailsColumn message that opens the owner,
 *                slot: where the owner puts the file, name: file name, meta: free data
 *                passed back with the result
 * @returns { jobId, promise } where the promise resolves to { id, filename, ext }
 */
export function enqueue (source, { owner = null, ownerLabel = '', open = null, slot = null, name = '', meta = null } = {}) {
  const id = 'upload-' + (++sequence)
  const shopId = store.getters.shopId
  const job = {
    source,
    shopId,
    url: store.getters.baseDomain + shopId + '/files/' + (source && source.import ? 'import' : 'new') + '.json?apikey=' + store.getters.apiKey,
    name: name || (source && source.name) || '',
    controller: null
  }
  createPromise(job)
  jobs.set(id, job)
  store.commit('addUpload', {
    id,
    shopId,
    name: job.name,
    owner,
    ownerLabel,
    open,
    slot,
    meta,
    status: 'queued',
    progress: 0,
    error: null,
    result: null,
    applied: false
  })
  waiting.push(id)
  pump()
  return { jobId: id, promise: job.promise }
}

function pump () {
  while (running < MAX_CONCURRENT && waiting.length) {
    run(waiting.shift())
  }
}

async function run (id) {
  const job = jobs.get(id)
  if (!job) {
    pump()
    return
  }
  running++
  job.controller = new AbortController()
  const signal = job.controller.signal
  try {
    let response
    if (job.source && job.source.import) {
      // The server downloads and resizes the image
      update(id, { status: 'processing', progress: 100 })
      response = await axios.post(job.url, job.source.import, { signal })
    } else {
      let blob = job.source
      let name = job.name
      if (typeof blob === 'function') {
        update(id, { status: 'fetching' })
        const fetched = await blob(signal)
        blob = fetched.blob
        name = fetched.name || name
        update(id, { name })
      }
      update(id, { status: 'uploading', progress: 0 })
      const formData = new FormData()
      formData.append('files[]', blob, name || 'image')
      response = await axios.post(job.url, formData, {
        signal,
        onUploadProgress: (e) => {
          const progress = e.total ? Math.min(100, Math.round(e.loaded * 100 / e.total)) : 0
          // When all bytes are sent the server still resizes the image
          update(id, { progress, status: progress >= 100 ? 'processing' : 'uploading' })
        }
      })
    }
    if (!response.data || !response.data.success) {
      throw new Error((response.data && response.data.error) || 'Upload failed')
    }
    const file = {
      id: response.data.id,
      filename: response.data.filename,
      ext: response.data.ext || extensionOf(response.data.filename)
    }
    update(id, { status: 'done', progress: 100, result: file })
    job.resolve(file)
    const upload = stateOf(id)
    eventBus.$emit('uploadCompleted', { jobId: id, shopId: job.shopId, owner: upload.owner, slot: upload.slot, meta: upload.meta, file })
    scheduleClear(id)
  } catch (e) {
    if (isCanceled(e)) {
      update(id, { status: 'canceled' })
      setTimeout(() => remove(id), 1000)
    } else {
      update(id, { status: 'error', error: errorMessage(e) })
    }
    job.reject(e)
    eventBus.$emit('uploadFailed', { jobId: id, shopId: job.shopId, owner: stateOf(id)?.owner, error: e })
  } finally {
    job.controller = null
    running--
    pump()
  }
}

// Finished uploads disappear from the tray after a moment, unless they still
// wait for their editor to be opened
function scheduleClear (id) {
  setTimeout(() => {
    const upload = stateOf(id)
    if (upload && upload.status === 'done' && (!upload.owner || upload.applied)) {
      remove(id)
    }
  }, CLEAR_DONE_DELAY)
}

function remove (id) {
  jobs.delete(id)
  store.commit('removeUpload', id)
}

export function cancel (id) {
  const index = waiting.indexOf(id)
  if (index !== -1) {
    waiting.splice(index, 1)
    const job = jobs.get(id)
    update(id, { status: 'canceled' })
    job.reject(new axios.CanceledError())
    setTimeout(() => remove(id), 1000)
    return
  }
  const job = jobs.get(id)
  if (job && job.controller) {
    job.controller.abort()
  }
}

export function retry (id) {
  const job = jobs.get(id)
  const upload = stateOf(id)
  if (!job || !upload || upload.status !== 'error') {
    return null
  }
  createPromise(job)
  update(id, { status: 'queued', progress: 0, error: null })
  waiting.push(id)
  pump()
  return job.promise
}

// Remove a failed upload, or a pending attachment the user doesn't want
export function dismiss (id) {
  const upload = stateOf(id)
  if (upload && !ACTIVE_STATUSES.includes(upload.status)) {
    remove(id)
  }
}

export function hasActive () {
  return !!store && store.state.uploads.some(u => ACTIVE_STATUSES.includes(u.status))
}

export function activeJobsFor (owner) {
  return store.state.uploads.filter(u => u.owner === owner && ACTIVE_STATUSES.includes(u.status))
}

export function promiseFor (id) {
  const job = jobs.get(id)
  return job ? job.promise : Promise.resolve(null)
}

// Finished uploads of an owner that no open editor has applied yet. They are
// marked applied, so they are handed out only once. `accepts` limits them to
// the slots the editor shows.
export function takePending (owner, accepts = () => true) {
  const pending = store.state.uploads.filter(u => u.owner === owner && u.status === 'done' && !u.applied && accepts(u.slot))
  for (const upload of pending) {
    markApplied(upload.id)
  }
  return pending.map(u => ({ jobId: u.id, slot: u.slot, meta: u.meta, file: u.result }))
}

export function markApplied (id, applied = true) {
  const upload = stateOf(id)
  if (!upload) {
    return
  }
  update(id, { applied })
  if (applied) {
    scheduleClear(id)
  }
}
